import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {Link, useLoaderData, useRouteLoaderData} from '@remix-run/react';
import {seoMeta} from '~/domains/organization/utilities/seoMeta';
import {
  parsePublicationParams,
  hasActiveFilters,
} from '~/domains/research/utilities/parsePublicationParams';
import {PublicationsFilter} from '~/domains/research/components/PublicationsFilter';
import {PublicationListItem} from '~/domains/research/components/PublicationListItem';
import {PublicationsPagination} from '~/domains/research/components/PublicationsPagination';
import type {PublicacoesLayoutLoaderData} from './publicacoes';

interface TypePreview {
  type: Research.PublicationType;
  label: string;
  href: string;
  count: number;
  items: Research.PublicPublicationDto[];
}

interface HubDefaultData {
  mode: 'overview';
  previews: TypePreview[];
}

interface HubSearchData {
  mode: 'search';
  results: {
    items: Research.PublicPublicationDto[];
    total: number;
    page: number;
    totalPages: number;
  };
  counts: Research.PublicationTypeCounts;
}

type PublicacoesLoaderData = HubDefaultData | HubSearchData;

export const meta: MetaFunction = function PublicacoesMeta({matches}) {
  return seoMeta('publicacoes', {
    title: 'Publicações - Cindedi',
    description:
      'Confira as publicações do CINDEDI! Acesse artigos acadêmicos, livros e capítulos que refletem nossas pesquisas em desenvolvimento humano e educação.',
  }, matches);
};

export const loader: LoaderFunction = async function PublicacoesLoader({request, context: {use}}) {
  const Publication = use('Publication');
  const url = new URL(request.url);
  const params = parsePublicationParams(url.searchParams);

  if (hasActiveFilters(params)) {
    // Search/filter mode: paginated cross-type results
    const [result, counts] = await Promise.all([
      Publication.query(params),
      Publication.countByType({q: params.q, year: params.year, decade: params.decade, stream: params.stream}),
    ]);

    return json<HubSearchData>({
      mode: 'search',
      results: {
        items: result.items as unknown as Research.PublicPublicationDto[],
        total: result.total,
        page: result.page,
        totalPages: result.totalPages,
      },
      counts,
    });
  }

  // Overview mode: recent items per type
  const [recent, counts] = await Promise.all([
    Publication.recentByType(5, params.sort),
    Publication.countByType(),
  ]);

  const previews: TypePreview[] = [
    {
      type: 'article',
      label: 'Artigos',
      href: '/publicacoes/artigos',
      count: counts.article,
      items: recent.article as unknown as Research.PublicPublicationDto[],
    },
    {
      type: 'bookChapter',
      label: 'Capítulos de Livro',
      href: '/publicacoes/capitulos-de-livro',
      count: counts.bookChapter,
      items: recent.bookChapter as unknown as Research.PublicPublicationDto[],
    },
    {
      type: 'book',
      label: 'Livros',
      href: '/publicacoes/livros',
      count: counts.book,
      items: recent.book as unknown as Research.PublicPublicationDto[],
    },
  ];

  return json<HubDefaultData>({mode: 'overview', previews});
};

export default function PublicacoesView() {
  const data = useLoaderData() as unknown as PublicacoesLoaderData;
  const layoutData = useRouteLoaderData('routes/_public+/publicacoes') as PublicacoesLayoutLoaderData;

  if (data.mode === 'search') {
    return (
      <>
        <PublicationsFilter
          streams={layoutData.streams}
          basePath="/publicacoes"
        />
        <SearchResults data={data} />
      </>
    );
  }

  return (
    <>
      <PublicationsFilter
        streams={layoutData.streams}
        basePath="/publicacoes"
      />
      <OverviewView previews={data.previews} />
    </>
  );
}

function OverviewView({previews}: {previews: TypePreview[]}) {
  return (
    <div className="flex flex-col gap-6">
      {previews.map((preview) => (
        <section key={preview.type}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-shade-12">
              {preview.label}
              <span className="ml-2 text-sm font-normal text-shade-10">({preview.count})</span>
            </h2>
            <Link
              to={preview.href}
              className="text-sm text-primary-11 hover:text-primary-12 font-medium"
            >
              Ver todos →
            </Link>
          </div>
          {preview.items.length > 0 ? (
            <ul role="list" className="flex flex-col gap-2">
              {preview.items.map((publication) => (
                <PublicationListItem key={publication.id} publication={publication} />
              ))}
            </ul>
          ) : (
            <p className="text-shade-10 text-sm">Nenhuma publicação encontrada.</p>
          )}
        </section>
      ))}
    </div>
  );
}

function SearchResults({data}: {data: HubSearchData}) {
  const {results, counts} = data;

  if (results.total === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-shade-11">Nenhuma publicação encontrada com os filtros aplicados.</p>
      </div>
    );
  }

  // Group results by type for display
  const grouped = {
    article: results.items.filter((p) => p.type === 'article'),
    bookChapter: results.items.filter((p) => p.type === 'bookChapter'),
    book: results.items.filter((p) => p.type === 'book'),
  };

  const typeLabels: Record<Research.PublicationType, string> = {
    article: 'Artigos',
    bookChapter: 'Capítulos de Livro',
    book: 'Livros',
  };

  // Only show per-type breakdown if results span multiple types
  const activeTypes = [counts.article > 0, counts.bookChapter > 0, counts.book > 0].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-shade-11">
        {results.total} {results.total === 1 ? 'resultado' : 'resultados'}
        {activeTypes > 1 && (
          <>
            {counts.article > 0 && ` · ${counts.article} artigos`}
            {counts.bookChapter > 0 && ` · ${counts.bookChapter} capítulos`}
            {counts.book > 0 && ` · ${counts.book} livros`}
          </>
        )}
      </p>

      {(['article', 'bookChapter', 'book'] as const).map((type) => {
        const items = grouped[type];
        if (items.length === 0) return null;
        return (
          <section key={type}>
            <h3 className="text-sm font-semibold text-shade-11 mb-2">{typeLabels[type]}</h3>
            <ul role="list" className="flex flex-col gap-2">
              {items.map((publication) => (
                <PublicationListItem key={publication.id} publication={publication} />
              ))}
            </ul>
          </section>
        );
      })}

      <PublicationsPagination
        currentPage={results.page}
        totalPages={results.totalPages}
        total={results.total}
      />
    </div>
  );
}
